// Estado e regras do formulário do Report de Momento X (fluxo CX, `report_version = 2`).
//
// Usado pela página do Report (`appointment-report`) e pelo painel de Momento X da central de pendências. Tudo o que
// o mixin cria tem o prefixo `mx` (dados em `mxReport`, computeds/métodos `mx*`) para não colidir com a página.
//
// ── Uso ─────────────────────────────────────────────────────────────────────────────────────────────────────────
//   mixins: [momentXReportForm],
//   created() { this.mxInit(this.id) },          // e `watch: { id: 'mxInit' }` se a rota reaproveitar o componente
//
//   <report-yes-no
//     v-for="key in mxVisibleQuestions" :key="key" :ref="mxItemRef(key)"
//     v-bind="mxYesNoProps(key)" v-on="mxListeners(key)"
//   >
//     <template #follow-up>
//       <report-reason-select v-if="mxFollowUpOf(key)" :ref="mxItemRef(mxFollowUpOf(key))"
//         v-bind="mxReasonProps(mxFollowUpOf(key))" v-on="mxListeners(mxFollowUpOf(key))" />
//     </template>
//   </report-yes-no>
//   <report-date-lock-banner v-if="mxShowDateLock" v-bind="mxDateLockProps" @released="mxOnReleased" />
//   <report-action-bar v-if="mxShowActionBar" v-bind="mxActionBarProps"
//     @finalize="mxFinalize" @jump-to-missing="mxJumpToMissing()" />
//   <report-success v-if="mxIsDone" v-bind="mxSuccessProps"> … </report-success>
//   <span class="ar-sr-only" aria-live="polite">{{ mxReport.announce }}</span>
//
//   Painel da central (layout compacto: linhas baixas, select até 600px, "Finalizando…" ≥ 700ms, barra `pane`,
//   confirmação "Report salvo"): declare `mxCompact: true` no `data()` do componente.
//
// ── Regras (as mesmas do `FinalizeMomentXReportRequest`; ver `reportItems` em constants/appointment_report_v2) ──
//   "A Reunião foi realizada?" obrigatória · Não → só o motivo da não realização (obrigatório) · Sim → Negócio
//   fechado, Oferta e Especialista obrigatórias · Oferta = Não → motivo da oferta obrigatório. Respostas de um
//   ramo escondido ficam guardadas (e no rascunho) e voltam se a resposta mudar, mas nunca vão no finalize.
//
// ── Estados (`mxReport.status`, ver MX_REPORT_STATUS) ─────────────────────────────────────────────────────────
//   loading → waiting (antes do horário) | editing → finalizing → saved
//                                         editing ⇄ error (rede/5xx: respostas mantidas, "Tentar novamente")
//   already_reported (409 / report existente: somente leitura) · unavailable (422 NOT_OWNER / WRONG_TYPE /
//   NOT_REPORTABLE) · load_error.
//
// ── Ganchos opcionais da página ──────────────────────────────────────────────────────────────────────────────
//   onMomentXReportFinalized(data)   — data = `{ report, replayed, pending_count }`
//   onMomentXReportAlreadyReported(report)
//   onMomentXReportPendingRejected({ appointmentId, pendingIds }) — 423 da trava para um compromisso que saiu das
//     pendências sem report (ver `mxApplyPendingRejection`)
//   (também emitidos como eventos `mx-report-finalized` / `mx-report-already-reported` / `mx-report-pending-rejected`)
//
// A trava (`account/reportLock`) é atualizada pela action FINALIZE_MX_REPORT: libera o compromisso localmente e
// dispara `REFRESH_REPORT_LOCK({ force: true })` em segundo plano — por isso o mixin não repete a chamada.
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as appointmentTypes from '@/modules/appointment/store/types'
import {
  APP_COPY,
  FIGMA_COPY,
  HAD_MEETING,
  NEW_COPY,
  QUESTIONS,
  REASON_SELECTS,
  REPORT_ERROR_CODES,
  buildFinalizePayload,
  isYesNoAnswered,
  missingReportItems,
  questionLabel,
  reportItems,
} from '@/constants/appointment_report_v2'
import {
  apiErrorToast,
  getApiErrorMessage,
  hasApiErrorCode,
  isReportNotOpenError,
  isReportPendingError,
} from '@/helpers/error_messages'
import {
  answersFromReport,
  emptyReportAnswers,
  formatDateTime,
  newReportIdempotencyKey,
  nextReportUid,
  sanitizeReportAnswers,
  scrollIntoViewAndFocus,
  toTimestamp,
} from '../helpers/report_ui'

export const MX_REPORT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOAD_ERROR: 'load_error',
  WAITING: 'waiting',
  EDITING: 'editing',
  FINALIZING: 'finalizing',
  ERROR: 'error',
  SAVED: 'saved',
  ALREADY_REPORTED: 'already_reported',
  UNAVAILABLE: 'unavailable',
}

// Autosave do rascunho: 800ms depois da última resposta.
export const DRAFT_SAVE_DEBOUNCE_MS = 800
// "Finalizando…" fica visível no mínimo 900ms (board v4-report) — um sucesso instantâneo parece falha.
export const FINALIZE_MIN_BUSY_MS = 900
// No painel da central, 700ms (board v4-pendencias).
export const FINALIZE_MIN_BUSY_MS_COMPACT = 700
// Autosave que falhou (rede/5xx): nova tentativa com espera crescente, até 1 min.
const DRAFT_RETRY_BASE_MS = 5000
const DRAFT_RETRY_MAX_MS = 60000
// Controles entrando (30ms de intervalo) quando o formulário é liberado no horário.
const RELEASE_FX_MS = 450
const ENTER_STAGGER_MS = 30
// Linhas que voltam quando "A Reunião foi realizada?" passa de Não para Sim.
const ROWS_FX_MS = 360

const UNAVAILABLE_CODES = [
  REPORT_ERROR_CODES.NOT_OWNER,
  REPORT_ERROR_CODES.WRONG_TYPE,
  REPORT_ERROR_CODES.NOT_REPORTABLE,
]

const EDITABLE_STATUSES = [MX_REPORT_STATUS.EDITING, MX_REPORT_STATUS.ERROR]
const DONE_STATUSES = [MX_REPORT_STATUS.SAVED, MX_REPORT_STATUS.ALREADY_REPORTED]

const QUESTION_KEYS = QUESTIONS.map(question => question.key)
// Pergunta → motivo que ela libera (`not_held_reason` sob "realizada?", `offer_not_made_reason` sob "oferta?").
const FOLLOW_UPS = Object.keys(REASON_SELECTS).reduce((map, reasonKey) => ({
  ...map,
  [REASON_SELECTS[reasonKey].parent]: reasonKey,
}), {})

const wait = ms => new Promise(resolve => {
  setTimeout(resolve, Math.max(0, ms))
})

const responseData = error => (error && error.response && error.response.data) || {}
const responseStatus = error => error && error.response && error.response.status

const localClock = () => {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

const initialState = () => ({
  appointmentId: null,
  status: MX_REPORT_STATUS.IDLE,
  answers: emptyReportAnswers(),
  // Tentou finalizar com respostas faltando → as mensagens "Responda esta pergunta…" aparecem.
  attempted: false,
  touched: {},
  // Mensagens do 422 do servidor por campo (`{ not_held_reason: '…' }`).
  serverErrors: {},
  appointment: null,
  opensAt: null,
  serverNow: null,
  serverNowReceivedAt: null,
  draftVersion: null,
  draftSavedAt: null,
  draftDirty: false,
  draftSaveFailed: false,
  report: null,
  replayed: false,
  pendingCount: null,
  errorMessage: '',
  unavailableCode: null,
  // Liberado no horário com a tela aberta: o banner fica em "Formulário liberado" e os controles entram.
  unlocked: false,
  releasedFx: false,
  rowsFx: false,
  announce: '',
})

export default {
  data() {
    return {
      mxUid: nextReportUid('mx'),
      mxReport: initialState(),
      // Layout do painel da central (o componente sobrescreve no próprio `data()`).
      mxCompact: false,
    }
  },
  computed: {
    mxStatus() {
      return this.mxReport.status
    },
    mxIsLoading() {
      return this.mxReport.status === MX_REPORT_STATUS.LOADING
    },
    mxIsWaiting() {
      return this.mxReport.status === MX_REPORT_STATUS.WAITING
    },
    mxIsEditable() {
      return EDITABLE_STATUSES.includes(this.mxReport.status)
    },
    mxIsFinalizing() {
      return this.mxReport.status === MX_REPORT_STATUS.FINALIZING
    },
    mxHasError() {
      return this.mxReport.status === MX_REPORT_STATUS.ERROR
    },
    mxIsDone() {
      return DONE_STATUSES.includes(this.mxReport.status)
    },
    mxItems() {
      return reportItems(this.mxReport.answers)
    },
    mxMissingItems() {
      return missingReportItems(this.mxReport.answers)
    },
    mxMissingCount() {
      return this.mxMissingItems.length
    },
    mxIsComplete() {
      return this.mxMissingCount === 0
    },
    // Corpo do finalize: só o caminho ativo.
    mxFinalizePayload() {
      return buildFinalizePayload(this.mxReport.answers)
    },
    // Perguntas Sim | Não a renderizar, na ordem do Figma. Aguardando horário: todas (prévia inerte).
    mxVisibleQuestions() {
      if (this.mxIsWaiting) return QUESTION_KEYS
      return this.mxReport.answers[HAD_MEETING] === false ? [HAD_MEETING] : QUESTION_KEYS
    },
    mxShowActionBar() {
      return this.mxIsEditable || this.mxIsFinalizing
    },
    // Banner da trava por horário: contagem enquanto aguarda; depois de liberar com a tela aberta, continua como
    // "Formulário liberado" (tom de sucesso) até finalizar.
    mxShowDateLock() {
      if (this.mxIsWaiting) return true
      return this.mxReport.unlocked && (this.mxIsEditable || this.mxIsFinalizing)
    },
    mxFinalizeMinBusyMs() {
      return this.mxCompact ? FINALIZE_MIN_BUSY_MS_COMPACT : FINALIZE_MIN_BUSY_MS
    },
    mxStepperState() {
      if (this.mxIsDone) return 'done'
      return this.mxIsWaiting ? 'locked' : 'active'
    },
    mxMeetingUrl() {
      const { appointment } = this.mxReport
      return appointment && appointment.meeting_format !== 'in_pearson' ? (appointment.meeting_room_link || '') : ''
    },
    mxDateLockProps() {
      return {
        opensAt: this.mxReport.opensAt || '',
        serverNow: this.mxReport.serverNow || '',
        serverNowReceivedAt: this.mxReport.serverNowReceivedAt,
        meetingUrl: this.mxMeetingUrl,
      }
    },
    mxActionBarProps() {
      return {
        missingCount: this.mxMissingCount,
        busy: this.mxIsFinalizing,
        error: this.mxHasError,
        errorMessage: this.mxReport.errorMessage,
        savedAt: this.mxReport.draftSavedAt || '',
        ctaLabel: FIGMA_COPY.finalizeButton,
        variant: this.mxCompact ? 'pane' : 'page',
        entering: this.mxReport.releasedFx,
      }
    },
    mxSuccessProps() {
      const { report, appointment } = this.mxReport
      const customerName = appointment && appointment.customer ? String(appointment.customer.name || '') : ''
      const props = {
        report,
        answers: report ? null : this.mxReport.answers,
        appointment,
        customerName,
      }
      if (!this.mxCompact) return props

      // Painel da central: "Report salvo" + "Emir Salin · Momento X · 22/02/26 | 13:00" (board v4-pendencias).
      const eventType = (appointment && appointment.event_type_description) || APP_COPY.momentX
      const when = appointment ? formatDateTime(appointment.appointment_start, ' | ') : ''
      return {
        ...props,
        compact: true,
        title: NEW_COPY.reportSaved,
        caption: [customerName, eventType, when].filter(Boolean).join(' · '),
      }
    },
  },
  created() {
    this.$_mxDraftTimer = null
    this.$_mxDraftRetryTimer = null
    this.$_mxDraftRetryDelay = DRAFT_RETRY_BASE_MS
    this.$_mxDraftRequest = null
    this.$_mxDraftQueued = false
    this.$_mxFinalizing = false
    this.$_mxIdempotencyKey = null
    this.$_mxLoadSeq = 0
    this.$_mxReleaseTimer = null
    this.$_mxRowsFxTimer = null
    this.$_mxDestroyed = false
    this.$_mxOnBeforeUnload = () => {
      if (this.mxReport.draftDirty && this.mxIsEditable) this.mxSaveDraft()
    }
    window.addEventListener('beforeunload', this.$_mxOnBeforeUnload)
  },
  beforeDestroy() {
    // Sai da tela com resposta ainda não salva → salva agora (sem esperar o debounce nem o PUT em andamento).
    this.mxFlushDraft()
    this.$_mxDestroyed = true
    this.mxClearTimers()
    window.removeEventListener('beforeunload', this.$_mxOnBeforeUnload)
  },
  methods: {
    // ---------------------------------------------------------------------------------------------------------
    // Carregamento
    // ---------------------------------------------------------------------------------------------------------

    /** Começa (ou recomeça) o formulário para o compromisso `appointmentId`. */
    mxInit(appointmentId) {
      // Trocou de compromisso (ex.: outra pendência na central) com resposta ainda não salva: salva a do anterior.
      this.mxFlushDraft()
      this.mxClearTimers()
      this.$_mxDraftRequest = null
      this.$_mxDraftQueued = false
      this.$_mxFinalizing = false
      // Uma chave por rascunho: todas as tentativas de finalizar ESTE formulário reusam a mesma, e o servidor
      // devolve o report já gravado (`replayed`) em vez de criar outro — duplo clique, timeout, "Tentar novamente".
      this.$_mxIdempotencyKey = newReportIdempotencyKey()
      this.mxReport = { ...initialState(), appointmentId: appointmentId ? Number(appointmentId) : null }

      return this.mxLoad()
    },

    mxLoad() {
      const id = this.mxReport.appointmentId
      if (!id) return Promise.resolve(null)
      this.$_mxLoadSeq += 1
      const sequence = this.$_mxLoadSeq
      this.mxReport.status = MX_REPORT_STATUS.LOADING
      this.mxReport.errorMessage = ''

      return this.$store.dispatch(appointmentTypes.GET_REPORT_DRAFT, id)
        .then(resp => {
          if (sequence !== this.$_mxLoadSeq || this.$_mxDestroyed) return null
          if (!resp || !resp.data) throw new Error('Resposta vazia ao carregar o report.')
          this.mxApplyShowResponse(resp.data)
          return resp.data
        })
        .catch(error => {
          if (sequence !== this.$_mxLoadSeq || this.$_mxDestroyed) return null
          this.mxHandleLoadError(error)
          return null
        })
    },

    /** Resposta de GET appointments/{id}/report/draft → estado da tela. */
    mxApplyShowResponse(data) {
      const body = data || {}
      this.mxReport.appointment = body.appointment || null
      this.mxReport.opensAt = body.report_opens_at
        || (body.appointment && body.appointment.appointment_start)
        || null
      this.mxReport.serverNow = body.server_now || null
      this.mxReport.serverNowReceivedAt = Date.now()

      if (body.report) {
        this.mxApplyExistingReport(body.report)
        return
      }

      const { draft } = body
      this.mxReport.answers = sanitizeReportAnswers(draft && draft.answers)
      this.mxReport.draftVersion = draft && Number.isInteger(draft.version) ? draft.version : null
      this.mxReport.draftSavedAt = (draft && draft.updated_at) || null
      this.mxReport.draftDirty = false
      this.mxReport.status = this.mxIsBeforeOpening() ? MX_REPORT_STATUS.WAITING : MX_REPORT_STATUS.EDITING
    },

    mxApplyExistingReport(report) {
      this.mxClearTimers()
      this.mxReport.report = report
      this.mxReport.answers = answersFromReport(report)
      this.mxReport.draftDirty = false
      this.mxReport.status = MX_REPORT_STATUS.ALREADY_REPORTED
      this.mxCallHook('onMomentXReportAlreadyReported', report)
      this.$emit('mx-report-already-reported', report)
    },

    mxHandleLoadError(error) {
      if (this.mxApplyUnavailable(error)) return
      if (isReportPendingError(error) && this.mxApplyPendingRejection(error)) return
      this.mxReport.status = MX_REPORT_STATUS.LOAD_ERROR
      this.mxReport.errorMessage = isReportPendingError(error) ? '' : getApiErrorMessage(error, '')
    },

    /** Antes do horário pelo relógio do servidor (server_now + tempo decorrido desde a resposta). */
    mxIsBeforeOpening() {
      const opensAt = toTimestamp(this.mxReport.opensAt)
      if (opensAt === null) return false
      const serverNow = toTimestamp(this.mxReport.serverNow)
      const now = serverNow === null
        ? Date.now()
        : serverNow + (Date.now() - (this.mxReport.serverNowReceivedAt || Date.now()))

      return now < opensAt
    },

    /** 423 APPOINTMENT_REPORT_NOT_OPEN: volta para "aguardando horário" com o horário do servidor. */
    mxApplyNotOpen(body) {
      const data = body || {}
      if (data.opens_at) this.mxReport.opensAt = data.opens_at
      if (data.server_now) this.mxReport.serverNow = data.server_now
      this.mxReport.serverNowReceivedAt = Date.now()
      this.mxClearTimers()
      this.mxReport.unlocked = false
      this.mxReport.releasedFx = false
      this.mxReport.status = MX_REPORT_STATUS.WAITING
    },

    /** O banner de horário chegou a zero. */
    mxOnReleased(payload) {
      if (!this.mxIsWaiting) return
      this.mxReport.status = MX_REPORT_STATUS.EDITING
      if (payload && payload.immediate) {
        // Já estava liberado ao montar o banner: sem banner nem animação.
        this.mxReport.unlocked = false
        if (this.mxReport.draftDirty) this.mxScheduleDraftSave()
        return
      }
      // O banner fica em "Formulário liberado" (e anuncia via aria-live); os controles entram escalonados.
      this.mxReport.unlocked = true
      this.mxReport.releasedFx = true
      clearTimeout(this.$_mxReleaseTimer)
      this.$_mxReleaseTimer = setTimeout(() => {
        this.mxReport.releasedFx = false
      }, RELEASE_FX_MS)
      // Uma resposta pendente de antes da trava (ex.: 423 no autosave) segue agora.
      if (this.mxReport.draftDirty) this.mxScheduleDraftSave()
    },

    /** 422 de dono/tipo/status: a tela mostra a mensagem do servidor, sem formulário. */
    /**
     * 423 APPOINTMENT_REPORT_PENDING com este compromisso FORA dos `pending_ids`: ele saiu das pendências sem report
     * (cancelado, excluído ou transferido por outra pessoa) enquanto outras continuam — o servidor recusa rascunho e
     * finalize dele com a trava, e nunca chega ao 422 NOT_REPORTABLE. Fora da central o interceptor já leva o PN para
     * lá; na central ele não redireciona, então o formulário mostra o aviso (em vez de voltar a editar em silêncio,
     * com um "Finalizar" que nunca funciona) e avisa a tela, que tira o item da lista. `false` = ainda pendente.
     */
    mxApplyPendingRejection(error) {
      const ids = responseData(error).pending_ids
      const pendingIds = (Array.isArray(ids) ? ids : []).map(Number)
      const id = this.mxReport.appointmentId
      if (!id || pendingIds.includes(id)) return false
      this.mxClearTimers()
      this.mxReport.draftDirty = false
      this.mxReport.status = MX_REPORT_STATUS.UNAVAILABLE
      this.mxReport.unavailableCode = REPORT_ERROR_CODES.NOT_REPORTABLE
      this.mxReport.errorMessage = APP_COPY.notReportable
      const payload = { appointmentId: id, pendingIds }
      this.mxCallHook('onMomentXReportPendingRejected', payload)
      this.$emit('mx-report-pending-rejected', payload)

      return true
    },

    mxApplyUnavailable(error) {
      const { code } = responseData(error)
      if (responseStatus(error) !== 422 || !UNAVAILABLE_CODES.includes(code)) return false
      this.mxClearTimers()
      this.mxReport.status = MX_REPORT_STATUS.UNAVAILABLE
      this.mxReport.unavailableCode = code
      this.mxReport.errorMessage = getApiErrorMessage(error, '')
      return true
    },

    // ---------------------------------------------------------------------------------------------------------
    // Respostas e matriz de obrigatoriedade
    // ---------------------------------------------------------------------------------------------------------

    /** Grava uma resposta (Sim/Não = true/false; motivo = chave) e agenda o autosave. */
    mxSetAnswer(key, value) {
      if (!this.mxIsEditable) return false
      if (!Object.prototype.hasOwnProperty.call(this.mxReport.answers, key)) return false
      const next = value === undefined ? null : value
      const previous = this.mxReport.answers[key]
      if (previous === next) return false

      this.mxReport.answers[key] = next
      // "Não" → "Sim" na 1ª pergunta: as perguntas escondidas voltam com uma entrada curta (board v4-report).
      if (key === HAD_MEETING && previous === false && next === true) this.mxPlayRowsFx()
      this.$set(this.mxReport.touched, key, true)
      if (this.mxReport.serverErrors[key]) this.$delete(this.mxReport.serverErrors, key)
      // Mudou a resposta depois de uma falha: sai do estado de erro (o CTA volta a "Finalizar Momento X").
      if (this.mxReport.status === MX_REPORT_STATUS.ERROR) {
        this.mxReport.status = MX_REPORT_STATUS.EDITING
        this.mxReport.errorMessage = ''
      }
      this.mxScheduleDraftSave()
      return true
    },

    /** Motivo liberado pela pergunta `questionKey` quando ela está no valor que o exige (senão ''). */
    mxFollowUpOf(questionKey) {
      const reasonKey = FOLLOW_UPS[questionKey]
      if (!reasonKey || this.mxIsWaiting) return ''
      const { parent, when } = REASON_SELECTS[reasonKey]
      return this.mxReport.answers[parent] === when ? reasonKey : ''
    },

    /** O item está no caminho ativo (perguntas escondidas por "não realizada" ficam fora). */
    mxIsItemActive(key) {
      return this.mxItems.some(item => item.key === key)
    },

    mxIsItemMissing(key) {
      return this.mxMissingItems.some(item => item.key === key)
    },

    mxShowItemError(key) {
      if (!this.mxIsEditable && !this.mxIsFinalizing) return false
      return (this.mxReport.attempted && this.mxIsItemMissing(key)) || !!this.mxReport.serverErrors[key]
    },

    /** Glyph de status da pergunta: respondida E, se liberou um motivo, com o motivo escolhido. */
    mxIsItemComplete(questionKey) {
      if (!isYesNoAnswered(this.mxReport.answers[questionKey])) return false
      const reasonKey = this.mxFollowUpOf(questionKey)
      return !reasonKey || !this.mxIsItemMissing(reasonKey)
    },

    /** Nome do `ref` do componente do item (usado pelo "ir para a resposta que falta"). */
    mxItemRef(key) {
      return `mx-item-${key}`
    },

    mxYesNoProps(key) {
      const index = Math.max(0, this.mxVisibleQuestions.indexOf(key))
      return {
        value: this.mxReport.answers[key],
        legend: questionLabel(key),
        invalid: this.mxShowItemError(key),
        errorMessage: this.mxReport.serverErrors[key] || '',
        complete: this.mxIsItemComplete(key),
        readonly: !this.mxIsEditable,
        locked: this.mxIsWaiting,
        followUpVisible: !!this.mxFollowUpOf(key),
        compact: this.mxCompact,
        enterFx: this.mxReport.releasedFx,
        rowEnterFx: this.mxReport.rowsFx && key !== HAD_MEETING,
        enterDelay: index * ENTER_STAGGER_MS,
        idPrefix: `${this.mxUid}-${key}`,
      }
    },

    mxReasonProps(key) {
      return {
        value: this.mxReport.answers[key],
        reasonKey: key,
        invalid: this.mxShowItemError(key),
        errorMessage: this.mxReport.serverErrors[key] || '',
        readonly: !this.mxIsEditable,
        maxWidth: this.mxCompact ? '600px' : '520px',
        idPrefix: `${this.mxUid}-${key}`,
      }
    },

    mxPlayRowsFx() {
      this.mxReport.rowsFx = true
      clearTimeout(this.$_mxRowsFxTimer)
      this.$_mxRowsFxTimer = setTimeout(() => {
        this.$_mxRowsFxTimer = null
        this.mxReport.rowsFx = false
      }, ROWS_FX_MS)
    },

    mxListeners(key) {
      return {
        input: value => this.mxSetAnswer(key, value),
      }
    },

    /** Leva à primeira resposta que falta (ou a `preferredKey`), mostrando as mensagens de obrigatório. */
    mxJumpToMissing(preferredKey) {
      if (!this.mxIsEditable) return
      this.mxReport.attempted = true
      const preferred = typeof preferredKey === 'string' && this.mxIsItemActive(preferredKey) ? preferredKey : ''
      const first = this.mxMissingItems[0]
      const key = preferred || (first && first.key)
      if (!key) return
      this.$nextTick(() => this.mxFocusItem(key))
    },

    mxFocusItem(key) {
      let target = this.$refs[this.mxItemRef(key)]
      if (Array.isArray(target)) [target] = target
      scrollIntoViewAndFocus(target)
    },

    // ---------------------------------------------------------------------------------------------------------
    // Rascunho (autosave)
    // ---------------------------------------------------------------------------------------------------------
    mxScheduleDraftSave() {
      this.mxReport.draftDirty = true
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = setTimeout(() => {
        this.$_mxDraftTimer = null
        this.mxSaveDraft()
      }, DRAFT_SAVE_DEBOUNCE_MS)
    },

    /**
     * PUT do rascunho com TODAS as respostas (inclusive dos ramos escondidos, para restaurar). Um PUT por vez:
     * o próximo espera a `version` devolvida pelo anterior (senão o servidor acusaria conflito com a própria aba).
     */
    mxSaveDraft() {
      if (!this.mxReport.draftDirty || !this.mxIsEditable) return Promise.resolve(null)
      if (this.$_mxDraftRequest) {
        this.$_mxDraftQueued = true
        return this.$_mxDraftRequest
      }
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = null
      clearTimeout(this.$_mxDraftRetryTimer)
      this.$_mxDraftRetryTimer = null

      const id = this.mxReport.appointmentId
      this.mxReport.draftDirty = false
      // Resolve com o rascunho gravado (mesmo que a tela já tenha trocado de compromisso — `mxFlushDraft` usa a
      // versão devolvida) ou `null`; o estado da tela só muda se ainda for o mesmo compromisso.
      const request = this.$store.dispatch(appointmentTypes.SAVE_REPORT_DRAFT, {
        id,
        answers: { ...this.mxReport.answers },
        version: this.mxReport.draftVersion,
      })
        .then(resp => {
          const draft = resp && resp.data && resp.data.draft
          if (!draft) throw new Error('Resposta vazia ao salvar o rascunho.')
          if (id === this.mxReport.appointmentId) {
            // A versão vale mesmo se a tela já estiver finalizando: se o finalize falhar, o próximo PUT precisa dela.
            if (Number.isInteger(draft.version)) this.mxReport.draftVersion = draft.version
            this.mxReport.draftSavedAt = draft.updated_at || localClock()
            this.mxReport.draftSaveFailed = false
            this.$_mxDraftRetryDelay = DRAFT_RETRY_BASE_MS
          }
          return draft
        })
        .catch(error => {
          if (this.$_mxDestroyed || id !== this.mxReport.appointmentId) return null
          // Falhou enquanto finaliza: as respostas continuam por salvar; se o finalize falhar, o rascunho sai de novo.
          if (this.mxIsFinalizing) {
            this.mxReport.draftDirty = true
            return null
          }
          this.mxHandleDraftError(error)
          return null
        })
        .finally(() => {
          if (this.$_mxDraftRequest === request) this.$_mxDraftRequest = null
          if (this.$_mxDraftQueued) {
            this.$_mxDraftQueued = false
            if (!this.$_mxDestroyed && this.mxReport.draftDirty) this.mxSaveDraft()
          }
        })
      this.$_mxDraftRequest = request

      return request
    },

    /**
     * Salva JÁ a resposta ainda não enviada (sem debounce), encadeada no PUT em andamento para usar a versão que ele
     * devolver. Usado ao sair da tela e ao trocar de compromisso (o estado da tela vai ser descartado em seguida, por
     * isso o resultado não volta para ela). Nunca rejeita.
     */
    mxFlushDraft() {
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = null
      const { appointmentId: id, draftDirty, draftVersion } = this.mxReport
      if (!id || !draftDirty || !this.mxIsEditable) return Promise.resolve(null)

      const answers = { ...this.mxReport.answers }
      const previous = this.$_mxDraftRequest || Promise.resolve(null)
      this.mxReport.draftDirty = false
      this.$_mxDraftQueued = false

      return previous
        .then(draft => this.$store.dispatch(appointmentTypes.SAVE_REPORT_DRAFT, {
          id,
          answers,
          version: draft && Number.isInteger(draft.version) ? draft.version : draftVersion,
        }))
        .catch(() => null)
    },

    mxHandleDraftError(error) {
      // Finalizando / finalizado / fora do horário: a resposta do rascunho não muda mais nada.
      if (!this.mxIsEditable) return
      if (isReportPendingError(error)) {
        this.mxApplyPendingRejection(error)
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.DRAFT_CONFLICT)) {
        this.mxApplyDraftConflict(error)
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.ALREADY_REPORTED)) {
        this.mxHandleAlreadyReported()
        return
      }
      if (isReportNotOpenError(error)) {
        this.mxReport.draftDirty = true
        this.mxApplyNotOpen(responseData(error))
        return
      }
      if (this.mxApplyUnavailable(error)) return

      // Rede / 5xx / validação: as respostas continuam na tela; tenta de novo com espera crescente.
      this.mxReport.draftDirty = true
      this.mxReport.draftSaveFailed = true
      if (this.$_mxDestroyed) return
      clearTimeout(this.$_mxDraftRetryTimer)
      const delay = this.$_mxDraftRetryDelay
      this.$_mxDraftRetryDelay = Math.min(delay * 2, DRAFT_RETRY_MAX_MS)
      this.$_mxDraftRetryTimer = setTimeout(() => {
        this.$_mxDraftRetryTimer = null
        this.mxSaveDraft()
      }, delay)
    },

    /** 409 APPOINTMENT_REPORT_DRAFT_CONFLICT: outra aba salvou antes — carrega as respostas mais recentes. */
    mxApplyDraftConflict(error) {
      const { draft } = responseData(error)
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = null
      if (draft) {
        this.mxReport.answers = sanitizeReportAnswers(draft.answers)
        this.mxReport.draftVersion = Number.isInteger(draft.version) ? draft.version : null
        this.mxReport.draftSavedAt = draft.updated_at || this.mxReport.draftSavedAt
        this.mxReport.draftDirty = false
      } else {
        this.mxLoad()
      }
      this.mxToast({
        text: getApiErrorMessage(error, ''),
        icon: 'RefreshCwIcon',
        variant: 'warning',
      })
    },

    /** Descarta o rascunho no servidor e limpa as respostas. */
    mxDiscardDraft() {
      if (!this.mxIsEditable) return Promise.resolve(null)
      const id = this.mxReport.appointmentId
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = null

      return this.$store.dispatch(appointmentTypes.DISCARD_REPORT_DRAFT, id)
        .then(resp => {
          if (id !== this.mxReport.appointmentId) return null
          this.mxReport.answers = emptyReportAnswers()
          this.mxReport.draftVersion = null
          this.mxReport.draftSavedAt = null
          this.mxReport.draftDirty = false
          this.mxReport.attempted = false
          this.mxReport.touched = {}
          return resp
        })
    },

    // ---------------------------------------------------------------------------------------------------------
    // Finalizar
    // ---------------------------------------------------------------------------------------------------------

    /**
     * Finaliza o report. Idempotente: um clique durante o envio é ignorado e todas as tentativas usam a mesma
     * `Idempotency-Key`. Incompleto → leva à primeira resposta que falta. Resolve com `{ report, replayed,
     * pending_count }` no sucesso, ou `null`.
     */
    mxFinalize() {
      if (this.$_mxFinalizing || !this.mxIsEditable) return Promise.resolve(null)
      if (!this.mxIsComplete) {
        this.mxJumpToMissing()
        return Promise.resolve(null)
      }

      // Marca própria deste envio: um envio antigo (de antes de `mxInit`) não libera a trava de clique do atual.
      const attempt = {}
      this.$_mxFinalizing = attempt
      clearTimeout(this.$_mxDraftTimer)
      this.$_mxDraftTimer = null
      clearTimeout(this.$_mxDraftRetryTimer)
      this.$_mxDraftRetryTimer = null
      if (!this.$_mxIdempotencyKey) this.$_mxIdempotencyKey = newReportIdempotencyKey()

      const id = this.mxReport.appointmentId
      const startedAt = Date.now()
      const minBusyMs = this.mxFinalizeMinBusyMs
      const holdBusy = () => wait(minBusyMs - (Date.now() - startedAt))
      this.mxReport.status = MX_REPORT_STATUS.FINALIZING
      this.mxReport.errorMessage = ''
      this.mxReport.announce = ''

      return this.$store.dispatch(appointmentTypes.FINALIZE_MX_REPORT, {
        id,
        answers: { ...this.mxReport.answers },
        idempotencyKey: this.$_mxIdempotencyKey,
      })
        .then(
          resp => holdBusy().then(() => this.mxApplyFinalized(id, resp)),
          error => holdBusy().then(() => {
            this.mxHandleFinalizeError(id, error)
            // Voltou a editar com respostas que não chegaram ao rascunho (autosave cancelado/falho no envio).
            if (this.mxIsEditable && this.mxReport.draftDirty) this.mxScheduleDraftSave()
            return null
          }),
        )
        .finally(() => {
          if (this.$_mxFinalizing === attempt) this.$_mxFinalizing = false
        })
    },

    mxApplyFinalized(id, resp) {
      if (this.$_mxDestroyed || id !== this.mxReport.appointmentId) return null
      const data = (resp && resp.data) || {}
      const pendingCount = Number(data.pending_count)
      this.mxClearTimers()
      this.mxReport.report = data.report || null
      this.mxReport.replayed = !!data.replayed
      this.mxReport.pendingCount = Number.isInteger(pendingCount) && pendingCount >= 0 ? pendingCount : null
      this.mxReport.draftDirty = false
      this.mxReport.status = MX_REPORT_STATUS.SAVED
      this.mxReport.announce = FIGMA_COPY.savedTitle
      this.mxCallHook('onMomentXReportFinalized', data)
      this.$emit('mx-report-finalized', data)

      return data
    },

    mxHandleFinalizeError(id, error) {
      if (this.$_mxDestroyed || id !== this.mxReport.appointmentId) return
      // Trava: o interceptor já gravou o lock e levou à central (quando precisa) — só volta a editar, a menos que o
      // compromisso tenha saído das pendências (aí o formulário não tem mais como ser enviado).
      if (isReportPendingError(error)) {
        if (!this.mxApplyPendingRejection(error)) this.mxReport.status = MX_REPORT_STATUS.EDITING
        return
      }
      if (isReportNotOpenError(error)) {
        this.mxApplyNotOpen(responseData(error))
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.ALREADY_REPORTED)) {
        this.mxHandleAlreadyReported()
        return
      }
      if (this.mxApplyUnavailable(error)) return

      if (responseStatus(error) === 422) {
        // Validação do FormRequest (a matriz do front é a mesma, então é raro): marca os campos e volta a editar.
        const errors = responseData(error).errors || {}
        const serverErrors = {}
        Object.keys(errors).forEach(field => {
          if (!Object.prototype.hasOwnProperty.call(this.mxReport.answers, field)) return
          const messages = errors[field]
          serverErrors[field] = Array.isArray(messages) ? messages[0] : String(messages || '')
        })
        this.mxReport.serverErrors = serverErrors
        this.mxReport.status = MX_REPORT_STATUS.EDITING
        this.mxToast(apiErrorToast(error, NEW_COPY.finalizeError))
        this.mxJumpToMissing(Object.keys(serverErrors)[0])
        return
      }

      // Rede, timeout, 5xx: nunca mostra sucesso; respostas mantidas; "Tentar novamente" reusa a mesma chave.
      this.mxReport.status = MX_REPORT_STATUS.ERROR
      this.mxReport.errorMessage = NEW_COPY.finalizeError
    },

    /** 409 APPOINTMENT_ALREADY_REPORTED: mostra o report que já existe (somente leitura). */
    mxHandleAlreadyReported() {
      const id = this.mxReport.appointmentId
      this.mxClearTimers()
      this.mxReport.draftDirty = false
      this.mxReport.status = MX_REPORT_STATUS.ALREADY_REPORTED

      return this.$store.dispatch(appointmentTypes.GET_REPORT_DRAFT, id)
        .then(resp => {
          if (this.$_mxDestroyed || id !== this.mxReport.appointmentId) return
          const body = (resp && resp.data) || {}
          if (body.appointment) this.mxReport.appointment = body.appointment
          if (body.report) this.mxApplyExistingReport(body.report)
        })
        .catch(() => {
          // Sem o resumo: a tela continua em "já reportado" com as respostas locais.
        })
    },

    // ---------------------------------------------------------------------------------------------------------
    // Utilitários
    // ---------------------------------------------------------------------------------------------------------
    mxClearTimers() {
      clearTimeout(this.$_mxDraftTimer)
      clearTimeout(this.$_mxDraftRetryTimer)
      clearTimeout(this.$_mxReleaseTimer)
      clearTimeout(this.$_mxRowsFxTimer)
      this.$_mxDraftTimer = null
      this.$_mxDraftRetryTimer = null
      this.$_mxReleaseTimer = null
      this.$_mxRowsFxTimer = null
      // Sem o timer que as desliga, as classes de entrada ficariam presas.
      this.mxReport.releasedFx = false
      this.mxReport.rowsFx = false
    },

    mxCallHook(name, payload) {
      if (typeof this[name] === 'function') this[name](payload)
    },

    mxToast(props) {
      if (!this.$toast || !props || !props.text) return
      this.$toast({ component: ToastificationContent, props })
    },
  },
}
